import { Button } from '@tempo-io/sui-button';
import { ExternalLink, SettingsApplications } from '@tempo-io/sui-icons';
import { useTranslation } from 'react-i18next';
import type { SystemFieldsTabAdapter } from '@/components/modular-item-panel/adapters/types';
import { useTabAdapter } from '@/components/modular-item-panel/context/TabAdapterContext';
import { EmptyFieldValue } from '@/components/modular-item-panel/custom-fields/EmptyFieldValue';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';

export const PublishedSystemFields = () => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<SystemFieldsTabAdapter>();

  const createdBy = adapter.getFieldValue('createdBy');
  const createdAt = adapter.getFieldValue('createdAt');
  const updatedBy = adapter.getFieldValue('updatedBy');
  const updatedAt = adapter.getFieldValue('updatedAt');
  const doneDateTime = adapter.getFieldValue('doneDateTime');
  const externalKey = adapter.getFieldValue('externalKey');
  const externalLink = adapter.getFieldValue('externalLink');
  const planId = adapter.getFieldValue('planId');

  const formatDate = (dateString: string | null | undefined) => {
    return formatLocalDate(dateString ?? '', DateFormat.MONTH_LONG_DAY_SHORT_YEAR_TIME);
  };

  return (
    <div className="flex cursor-default flex-col gap-4">
      <div className="flex flex-row items-center gap-1 text-lg font-bold text-gray-500">
        <SettingsApplications /> {t('adaptivePlanner.itemPanel.systemFields')}
      </div>
      <div className="flex flex-col gap-2">
        {adapter.isVisible('createdBy') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.createdBy')}
            </div>
            {createdBy ? (
              <div className="text-sm text-gray-900">{createdBy}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('createdAt') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.createdOn')}
            </div>
            {createdAt ? (
              <div className="text-sm text-gray-900">{formatDate(createdAt)}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('updatedBy') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.updatedBy')}
            </div>
            {updatedBy ? (
              <div className="text-sm text-gray-900">{updatedBy}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('updatedAt') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.updatedOn')}
            </div>
            {updatedAt ? (
              <div className="text-sm text-gray-900">{formatDate(updatedAt)}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('doneDateTime') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.doneDate')}
            </div>
            {doneDateTime ? (
              <div className="text-sm text-gray-900">{formatDate(doneDateTime)}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('externalKey') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.externalKey')}
            </div>
            {externalKey && externalLink ? (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Button size="sm" variant="secondary" aria-label="open external link">
                  {externalKey}
                </Button>
                <ExternalLink className="cursor-pointer" />
              </a>
            ) : externalKey ? (
              <div className="text-sm text-gray-900">{externalKey}</div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}

        {adapter.isVisible('planId') && (
          <>
            <div className="text-sm font-medium">
              {t('adaptivePlanner.itemPanel.systemField.planId')}
            </div>
            {planId ? (
              <div className="text-sm text-gray-900">
                {t('adaptivePlanner.adaptivePlanner')}: {planId}
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </>
        )}
      </div>
    </div>
  );
};
